import { Alert } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils/validation';
import { translateKey } from '@frontend/translate';
import type { IRequest } from '../../../../interfaces/request';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import type { IServiceProvider } from '../../../procedures/serviceProvider';
import { InputRadioGroup, ItemBold } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ServiceOrganizationConsumerSelection } from '../../../../elements/ServiceOrganizationConsumerSelection';

interface IServiceOrganizationConsumerSelectorProps {
    errors: ValidationErrors;
    handleChange: <K extends keyof IProcedureAddEdit>(name: K, value: IProcedureAddEdit[K]) => Promise<void>;
    isHighPrivacy: boolean;
    procedure: IProcedureAddEdit;
    request: IRequest;
    service: IServiceGetOne;
    serviceProvider: IServiceProvider | undefined;
    validateOne: (name: keyof IProcedureAddEdit) => Promise<void>;
}

export function ServiceOrganizationConsumerSelector(props: IServiceOrganizationConsumerSelectorProps) {
    if (!props.request.isDirectProcedureAdded || !props.serviceProvider) {
        return null;
    }

    if (!props.serviceProvider.isServiceConsumerSelector) {
        if (props.isHighPrivacy) {
            return (
                <Alert appearance="info">
                    Je hebt momenteel niet de optie om een ontvanger binnen je organisatie voor dit onderzoek te kiezen.
                    Neem contact op met onze servicedesk.{' '}
                    <ItemBold>
                        Let op: bij verzenden van dit onderzoek gaat de uitslag nu naar de huisarts van de patiënt!
                    </ItemBold>
                </Alert>
            );
        }

        return null;
    }

    if (!props.isHighPrivacy && props.serviceProvider.canSelectCareProviderAsServiceConsumer) {
        return (
            <>
                <HorizontalFormGroup>
                    <FormLabel isRequired>{translateKey('request.careProviderIsAlsoRequester')}</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            name="is_de_huisarts_de_aanvrager"
                            onChange={async (value) => {
                                await props.handleChange('careProviderIsNotRequester', value === 'Ja' ? false : true);
                                if (props.procedure.careProviderIsNotRequester) {
                                    await props.handleChange('requesterOrganizationId', null);
                                    await props.handleChange('requesterPractitionerId', null);
                                }
                            }}
                            options={['Ja', 'Nee']}
                            value={props.procedure.careProviderIsNotRequester ? 'Nee' : 'Ja'}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {props.procedure.careProviderIsNotRequester &&
                    getServiceConsumerSelectorComponent(translateKey('request.whoIsTheRequester'))}
            </>
        );
    }

    return getServiceConsumerSelectorComponent(translateKey('common.selectConsumer'));

    function getServiceConsumerSelectorComponent(label: string) {
        if (!props.serviceProvider) {
            return null;
        }

        return (
            <ServiceOrganizationConsumerSelection
                error={props.errors.get('requesterOrganizationId') || props.errors.get('requesterPractitionerId')}
                hasErrors={props.errors.has('requesterOrganizationId') || props.errors.has('requesterPractitionerId')}
                initialOrganizationId={props.procedure ? props.procedure.requesterOrganizationId : null}
                initialPractitionerId={props.procedure ? props.procedure.requesterPractitionerId : null}
                label={label}
                onChange={async (organizationId, practitionerId) => {
                    await props.handleChange('requesterOrganizationId', organizationId);
                    await props.handleChange('requesterPractitionerId', practitionerId);
                }}
                onDoValidate={async () => {
                    await props.validateOne('requesterOrganizationId');
                    await props.validateOne('requesterPractitionerId');
                }}
                serviceId={props.service.id}
                serviceProviderId={props.serviceProvider && props.serviceProvider.id}
            />
        );
    }
}
